/**
 * useExpensePolicies — fetches and caches expense policy rules from the API.
 * Used by ExpenseModal, ChildEntryForm, and the policy rule builder.
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import { authService } from '@/lib/authService';
import type { ExpensePolicyRule } from '@/lib/finance/expense/policyRules';

interface UseExpensePoliciesResult {
  policies: ExpensePolicyRule[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useExpensePolicies(includeInactive = false): UseExpensePoliciesResult {
  const [policies, setPolicies] = useState<ExpensePolicyRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPolicies = useCallback(async () => {
    const token = authService.getTokens()?.access_token;
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (includeInactive) params.set('include_inactive', 'true');

      const res = await fetch(
        `/api/v1/finance/expense-policies${params.toString() ? `?${params.toString()}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch policies: ${res.status}`);
      }

      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setPolicies(data.data as ExpensePolicyRule[]);
      } else {
        setPolicies([]);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load expense policies';
      setError(msg);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void fetchPolicies();
  }, [fetchPolicies]);

  return { policies, loading, error, refetch: fetchPolicies };
}
